import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  NciAddendum,
  NciAddendumGenerationResult,
} from "models/patient-chart/nci-addendum";
import { Observable } from "rxjs";
import { ConfigurationService } from "../configuration/configuration.service";

@Injectable()
export class NciAddendumsService {
  private readonly apiUrl: string;

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.apiUrl = this.config.resolvePatientChartsUrl();
  }

  // TDR-16 / wave 15.5a (F1 closure): POST response widened from a bare base64
  // String to the JSON envelope NciAddendumGenerationResult{addendumId, pdf}.
  // `responseType` is left at the HttpClient default (json) so Angular parses
  // the body for us; consumers read `result.pdf` for the existing PDF download
  // path and `result.addendumId` for the F1 PATCH chain to OMF.
  createAndGenerateNciAddendums(
    patientChartId: number,
    nciAddendum: NciAddendum
  ): Observable<NciAddendumGenerationResult> {
    const url = `${this.apiUrl}/patient-charts/${patientChartId}/nci-addendums`;
    return this.http.post<NciAddendumGenerationResult>(url, nciAddendum);
  }

  /**
   * TRD-202 / REQ-005 (§4.6) — Upload a signed-copy attachment for an existing
   * NCI addendum. Multipart POST that wires the Edit Modal file picker to the
   * dedicated patient-charts endpoint shipped in wave 4b
   * ({@code POST /patient-charts/{patientChartId}/nci-addendum/{nciAddendumId}/attachment}).
   *
   * Server-side contract (TRD §4.6):
   *   - Accepts only {@code application/pdf}, {@code image/jpeg}, {@code image/png}.
   *   - Hard cap at 10 MB.
   *   - On success, replaces the row's {@code documentAttachmentId} (single-slot
   *     per §4.6 v1 default) and returns the updated addendum DTO.
   *
   * Error envelopes (§4.8 catalog) the caller is expected to dispatch on:
   *   - 413 + {@code {"code": "NCI_FILE_TOO_LARGE"}}
   *   - 415 + {@code {"code": "NCI_INVALID_FILE_FORMAT"}}
   *   - 500 + {@code {"code": "NCI_DOC_UPLOAD_FAILURE"}}
   *   - 403 + {@code {"code": "NCI_FORBIDDEN"}} when the caller lacks the role.
   *
   * Implementation notes mirroring the Document Management peer
   * ({@code DocManagementDataService.uploadDocument}):
   *   - Build the {@link FormData} with field name {@code "file"} — matches the
   *     controller's {@code @RequestParam("file") MultipartFile file} signature.
   *   - Do NOT set a {@code Content-Type} header here. Angular HttpClient lets
   *     the browser populate {@code multipart/form-data; boundary=...}
   *     automatically; setting it manually drops the boundary and the server
   *     rejects the payload as malformed.
   *   - The endpoint historically only existed as a controller; this is the
   *     first frontend wiring. There is no caching or retry — TRD §4.6 does
   *     not specify retries, and the user is in a modal capable of re-trying.
   */
  uploadAttachment(
    patientChartId: number,
    nciAddendumId: number,
    file: File
  ): Observable<NciAddendum> {
    const url = `${this.apiUrl}/patient-charts/${patientChartId}/nci-addendum/${nciAddendumId}/attachment`;
    const formData = new FormData();
    formData.append("file", file);
    return this.http.post<NciAddendum>(url, formData);
  }

  /**
   * CO-3640 (Determination Gate, §2.9) — Mark the mandatory hospice-election
   * determination complete for an addendum requirement that is currently
   * "Awaiting Determination". The server performs the single legal outbound
   * transition AWAITING_DETERMINATION -> READY_TO_FURNISH and returns the
   * updated addendum DTO (§2.2); the caller re-maps the row from that response
   * so it advances to "Ready to Furnish" and generation becomes available.
   *
   * Endpoint (no request body):
   *   POST /patient-charts/{patientChartId}/nci-addendum/{nciAddendumId}/mark-determination-complete
   *
   * Mirrors the base-URL + auth-interceptor pattern of the sibling calls above
   * (`createAndGenerateNciAddendums` / `uploadAttachment`). No client-side role
   * gate lives here — the 'Election of Benefits' EDIT permission is enforced at
   * the FE affordance (§2.10, see consents-details.ts) / gateway; the service is
   * intentionally thin.
   *
   * Error envelopes the caller may see (mirrors the sibling write paths):
   *   - 404 when the addendum does not exist / is cross-tenant.
   *   - 400 + {"code":"NCI_INVALID_STATUS_TRANSITION"} if the row is no longer
   *     Awaiting Determination.
   */
  markDeterminationComplete(
    patientChartId: number,
    nciAddendumId: number
  ): Observable<NciAddendum> {
    const url = `${this.apiUrl}/patient-charts/${patientChartId}/nci-addendum/${nciAddendumId}/mark-determination-complete`;
    return this.http.post<NciAddendum>(url, null);
  }
}



// WEBPACK FOOTER //
// ./src/providers/nci-addendums/nci-addendums-service.ts